import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { SeoHead } from '@/components/SeoHead';


interface TermsOfServiceProps {
  onBack: () => void;
}

export function TermsOfService({ onBack }: TermsOfServiceProps) {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Terms of Service — Sportara"
        description="Read the Sportara terms of service covering account use, subscriptions, user conduct, and our policies for players, parents, and coaches."
        path="/terms"
      />

      <header className="sticky top-0 z-10 bg-secondary/95 backdrop-blur-md border-b border-border/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0" aria-label="Go back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Logo className="w-6 h-6" alt="Sportara" />
            <span className="font-display font-bold text-lg">Terms of Service</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 prose prose-sm dark:prose-invert prose-headings:font-display">
        <h1 className="font-display font-bold text-3xl mb-2">Terms of Service</h1>
        <p className="text-muted-foreground text-sm">Last updated: March 19, 2026</p>

        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using the Sportara application ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.
        </p>

        <h2>2. Description of Service</h2>
        <p>
          Sportara is a sports education platform that provides interactive lessons, quizzes, and tools designed to improve your understanding of football, soccer, basketball, and other sports. The Service is currently in beta.
        </p>

        <h2>3. Eligibility</h2>
        <p>
          You must be at least 13 years old to create an account. Users under 18 must have permission from a parent or legal guardian. Parent accounts may create managed player profiles for children under their supervision.
        </p>

        <h2>4. Account Registration</h2>
        <p>
          You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate and complete information during registration.
        </p>

        <h2>5. User Conduct</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service for any unlawful purpose</li>
          <li>Attempt to gain unauthorized access to any part of the Service</li>
          <li>Interfere with or disrupt the Service or its servers</li>
          <li>Upload or transmit any harmful code or content</li>
          <li>Impersonate any person or entity</li>
        </ul>

        <h2>6. Intellectual Property</h2>
        <p>
          All content, features, and functionality of the Service — including text, graphics, logos, icons, and software — are the exclusive property of Sportara and are protected by copyright, trademark, and other intellectual property laws.
        </p>

        <h2>7. Subscriptions and Payments</h2>
        <p>
          Certain features may require a paid subscription. Subscription terms, pricing, and billing details are presented at the time of purchase. You may cancel your subscription at any time through your account settings. Refunds are handled in accordance with our refund policy.
        </p>

        <h2>8. Beta Disclaimer</h2>
        <p>
          The Service is currently in beta. Features may change, be removed, or experience interruptions. We provide the Service "as is" during this period and make no guarantees regarding availability or performance.
        </p>

        <h2>9. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, Sportara shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.
        </p>

        <h2>10. Termination</h2>
        <p>
          We may suspend or terminate your access to the Service at any time, with or without cause, and with or without notice. Upon termination, your right to use the Service ceases immediately.
        </p>

        <h2>11. Changes to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will notify users of material changes via the Service or email. Continued use after changes constitutes acceptance.
        </p>

        <h2>12. Contact</h2>
        <p>
          For questions about these Terms, contact us at <a href="mailto:appsportara@gmail.com" className="text-primary hover:underline">appsportara@gmail.com</a>.
        </p>
      </main>
    </div>
  );
}
